/**
 * Converts HSV color values to RGB using the standard algorithm
 * Based on the mathematical formula from computer graphics literature
 * Reference: Foley, van Dam, Feiner, Hughes - "Computer Graphics: Principles and Practice" (2nd Edition)
 * Also documented in Wikipedia: https://en.wikipedia.org/wiki/HSL_and_HSV#HSV_to_RGB
 * @param h Hue (0-360 degrees)
 * @param s Saturation (0-1)
 * @param v Value/Brightness (0-1)
 * @returns RGB values as integers 0-255
 */
interface RgbColor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

interface HsvColor {
  readonly h: number;
  readonly s: number;
  readonly v: number;
}

export const hsvToRgb = (h: number, s: number, v: number): RgbColor => {
  const chroma = v * s;
  const hPrime = h / 60;
  const x = chroma * (1 - Math.abs((hPrime % 2) - 1));
  const m = v - chroma;

  let rPrime = 0,
    gPrime = 0,
    bPrime = 0;

  if (hPrime >= 0 && hPrime < 1) {
    [rPrime, gPrime, bPrime] = [chroma, x, 0];
  } else if (hPrime >= 1 && hPrime < 2) {
    [rPrime, gPrime, bPrime] = [x, chroma, 0];
  } else if (hPrime >= 2 && hPrime < 3) {
    [rPrime, gPrime, bPrime] = [0, chroma, x];
  } else if (hPrime >= 3 && hPrime < 4) {
    [rPrime, gPrime, bPrime] = [0, x, chroma];
  } else if (hPrime >= 4 && hPrime < 5) {
    [rPrime, gPrime, bPrime] = [x, 0, chroma];
  } else if (hPrime >= 5 && hPrime < 6) {
    [rPrime, gPrime, bPrime] = [chroma, 0, x];
  }

  return {
    r: Math.round((rPrime + m) * 255),
    g: Math.round((gPrime + m) * 255),
    b: Math.round((bPrime + m) * 255),
  };
};

/**
 * Convenience function to convert HSV directly to hex
 * @param h Hue (0-360 degrees)
 * @param s Saturation (0-1)
 * @param v Value/Brightness (0-1)
 * @returns Hex color string like "#FF5733"
 */

export const hsvToHex = (h: number, s: number, v: number): string => {
  const rgb = hsvToRgb(h, s, v);

  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

/**
 * Converts RGB color values to HSV using the standard algorithm
 * Based on the mathematical formula from computer graphics literature
 * Reference: Foley, van Dam, Feiner, Hughes - "Computer Graphics: Principles and Practice" (2nd Edition)
 * Also documented in Wikipedia: https://en.wikipedia.org/wiki/HSL_and_HSV#RGB_to_HSV
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns HSV values where h is 0-360 degrees, s and v are 0-1
 */
export const rgbToHsv = (r: number, g: number, b: number): HsvColor => {
  // Normalize RGB values to 0-1 range
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  // Value is the maximum RGB component
  const v = max;

  // Saturation calculation
  const s = max === 0 ? 0 : delta / max;

  // Hue calculation
  let h = 0;
  if (delta !== 0) {
    if (max === rNorm) {
      h = ((gNorm - bNorm) / delta) % 6;
    } else if (max === gNorm) {
      h = (bNorm - rNorm) / delta + 2;
    } else {
      h = (rNorm - gNorm) / delta + 4;
    }
    h *= 60;

    // Ensure hue is positive
    if (h < 0) {
      h += 360;
    }
  }

  return {
    h: Math.round(h),
    s: Number(s.toFixed(3)),
    v: Number(v.toFixed(3)),
  };
};

/**
 * Converts RGB values to hexadecimal color string
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Hex color string like "#FF5733"
 */
export const rgbToHex = (r: number, g: number, b: number): string =>
  '#' +
  [r, g, b]
    .map((component) => {
      const hex = component.toString(16);

      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('');

/**
 * Converts hexadecimal color string to RGB values
 * @param hex Hex color string like "#FF5733", "FF5733", "#F53", or "F53"
 * @returns RGB values as integers 0-255
 */
export const hexToRgb = (hex: string): RgbColor => {
  // Remove the # if present
  const cleanHex = hex.replace('#', '');

  // Validate hex format (must be 3 or 6 characters)
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    throw new Error('Invalid hex color format. Expected format: #RGB, RGB, #RRGGBB, or RRGGBB');
  }

  let r: number, g: number, b: number;

  if (cleanHex.length === 3) {
    // Handle 3-character hex (e.g., "F53" -> "FF5533")
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else {
    // Handle 6-character hex (e.g., "FF5533")
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }

  return {
    r,
    g,
    b,
  };
};

/**
 * Converts hexadecimal color string to HSV values
 * @param hex Hex color string like "#FF5733" or "FF5733"
 * @returns HSV values where h is 0-360 degrees, s and v are 0-1
 */
export const hexToHsv = (hex: string): HsvColor => {
  const rgb = hexToRgb(hex);

  return rgbToHsv(rgb.r, rgb.g, rgb.b);
};

/**
 * Validates if a string is a valid hexadecimal color
 * @param value Color string to validate (with or without # prefix)
 * @returns true if valid 3 or 6 character hex color, false otherwise
 */
export const isValidHexColor = (value: string): boolean => {
  // Remove # if present
  const hexValue = value.startsWith('#') ? value.slice(1) : value;
  // Check if it's 3 or 6 characters and all are valid hex characters

  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hexValue);
};

/**
 * Ensures a hex color string is prefixed with # and validates it's a valid hex color
 * @param hexColor Hex color string with or without # prefix
 * @returns Hex color string with # prefix
 * @throws Error if the input is not a valid hex color
 */
export const ensureHexPrefix = (hexColor: string): string => {
  const color = hexColor.startsWith('#') ? hexColor : `#${hexColor}`;

  if (!isValidHexColor(color)) {
    throw new Error(`Invalid hex color format: ${hexColor}. Expected format: #RGB, RGB, #RRGGBB, or RRGGBB`);
  }

  return color;
};
